import { useEffect } from "react";

interface SeoProps {
  title: string;
  description: string;
  keywords: string;
  canonicalPath: string;
  ogImage?: string;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
}

const SITE_NAME = "瓜链 · 网红黑曝传播追踪";

function upsertMeta(attr: "name" | "property", key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel: string, href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

export default function Seo({
  title,
  description,
  keywords,
  canonicalPath,
  ogImage,
  jsonLd,
}: SeoProps) {
  useEffect(() => {
    const basePath = __BASE_PATH__ || "/";
    const prefix = basePath.endsWith("/") ? basePath.slice(0, -1) : basePath;
    const origin = window.location.origin;
    const cleanPath = canonicalPath.startsWith("/") ? canonicalPath : `/${canonicalPath}`;
    const canonical = `${origin}${prefix}${cleanPath === "/" ? "/" : cleanPath}`;
    const image = ogImage
      ? ogImage
      : "https://readdy.ai/api/search-image?query=abstract%20viral%20spread%20network%20visualization%20with%20glowing%20amber%20orange%20nodes%20on%20deep%20charcoal%20background%2C%20editorial%20data%20art%2C%20minimal%20composition%2C%20no%20text&width=1200&height=630&seq=gualian-og-cover&orientation=landscape";

    document.title = title;
    document.documentElement.lang = "zh-CN";

    upsertMeta("name", "description", description);
    upsertMeta("name", "keywords", keywords);
    upsertMeta("name", "robots", "index, follow, max-image-preview:large");
    upsertLink("canonical", canonical);

    upsertMeta("property", "og:type", "website");
    upsertMeta("property", "og:site_name", SITE_NAME);
    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:url", canonical);
    upsertMeta("property", "og:image", image);
    upsertMeta("property", "og:locale", "zh_CN");

    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "twitter:image", image);

    const scriptId = "gualian-jsonld";
    const prev = document.getElementById(scriptId);
    if (prev) prev.remove();
    if (jsonLd) {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.id = scriptId;
      script.textContent = JSON.stringify(jsonLd);
      document.head.appendChild(script);
    }
  }, [title, description, keywords, canonicalPath, ogImage, jsonLd]);

  return null;
}